import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Map, Edit3, Navigation, Crosshair, Loader2 } from 'lucide-react';
import '../../styles/leaflet.css';

// Import Leaflet dynamiquement pour éviter les problèmes SSR
let L: any = null;
let MapContainer: any = null;
let TileLayer: any = null;
let Marker: any = null;
let useMapEvents: any = null;
let useMap: any = null;

interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

interface LocationPickerProps {
  label: string;
  value: Coordinates | null;
  onSave: (coordinates: Coordinates) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

// Composant pour gérer les événements de la carte SANS changer le zoom
const MapEventHandler: React.FC<{ 
  onMapClick: (lat: number, lng: number) => void;
  onMapReady: (map: any) => void;
}> = ({ onMapClick, onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    if (map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  useMapEvents({
    click: (e: any) => {
      const { lat, lng } = e.latlng;
      // Juste mettre à jour les coordonnées, NE PAS toucher au zoom
      onMapClick(lat, lng);
    },
  });
  
  return null;
};

// Hook pour charger Leaflet dynamiquement
const useLeaflet = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        // Charger Leaflet et React-Leaflet
        const leafletModule = await import('leaflet');
        const reactLeafletModule = await import('react-leaflet');
        
        L = leafletModule.default;
        MapContainer = reactLeafletModule.MapContainer;
        TileLayer = reactLeafletModule.TileLayer;
        Marker = reactLeafletModule.Marker;
        useMapEvents = reactLeafletModule.useMapEvents;
        useMap = reactLeafletModule.useMap;

        // Configuration des icônes par défaut
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        setIsLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement de Leaflet:', error);
        setLoadError('Erreur de chargement de la carte');
      }
    };

    loadLeaflet();
  }, []);

  return { isLoaded, loadError };
};

export const LocationPicker: React.FC<LocationPickerProps> = ({
  label,
  value,
  onSave,
  disabled = false,
  placeholder = "Cliquez pour sélectionner la localisation"
}) => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [tempCoordinates, setTempCoordinates] = useState<Coordinates | null>(value);
  const [isEditingCoords, setIsEditingCoords] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [isTrackingGPS, setIsTrackingGPS] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [isLoadingGPS, setIsLoadingGPS] = useState(false);
  const mapInstanceRef = useRef<any>(null);
  const watchIdRef = useRef<number | null>(null);
  const { isLoaded, loadError } = useLeaflet();
  
  // Goma comme coordonnées par défaut pour la RDC
  const defaultCenter: [number, number] = [-1.6792, 29.2228];
  const initialZoom = 13;

  // ✨ SYNC: Synchroniser tempCoordinates quand value change depuis le parent
  useEffect(() => {
    if (!isMapOpen) {
      setTempCoordinates(value);
    }
  }, [value, isMapOpen]);

  // Synchroniser les champs manuels avec les coordonnées temporaires
  useEffect(() => {
    if (tempCoordinates) {
      setManualLat(tempCoordinates.latitude.toFixed(6));
      setManualLng(tempCoordinates.longitude.toFixed(6));
    }
  }, [tempCoordinates]);

  // Nettoyer le tracking GPS quand le modal se ferme
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  // Callback stable pour recevoir la référence de la carte
  const handleMapReady = useCallback((map: any) => {
    mapInstanceRef.current = map;
  }, []);

  // Callback stable pour les clics sur la carte
  const handleMapClick = useCallback((lat: number, lng: number) => {
    // Géocodage inverse simplifié pour obtenir une adresse approximative
    let address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    
    // Ajouter des noms de villes connues de RDC
    const knownCities = [
      { lat: -1.6792, lng: 29.2228, name: "Goma" },
      { lat: -4.3317, lng: 15.3139, name: "Kinshasa" },
      { lat: -11.6792, lng: 27.4794, name: "Lubumbashi" },
      { lat: -0.5177, lng: 25.1894, name: "Kisangani" },
      { lat: -5.8667, lng: 29.2500, name: "Bukavu" },
      { lat: -2.5053, lng: 28.8642, name: "Butembo" },
      { lat: -6.1344, lng: 23.5898, name: "Kananga" },
      { lat: -5.0500, lng: 18.8167, name: "Kikwit" },
      { lat: -8.7354, lng: 25.4836, name: "Kolwezi" },
      { lat: -5.9272, lng: 12.4086, name: "Matadi" },
      { lat: -2.9167, lng: 23.6500, name: "Mbandaka" },
      { lat: 2.1833, lng: 21.5167, name: "Gbadolite" },
    ];

    // Trouver la ville la plus proche (dans un rayon de ~50km)
    const nearestCity = knownCities.find(city => {
      const distance = Math.sqrt(
        Math.pow(city.lat - lat, 2) + Math.pow(city.lng - lng, 2)
      );
      return distance < 0.5; // Approximativement 50km
    });

    if (nearestCity) {
      address = `${nearestCity.name}, République Démocratique du Congo`;
    }

    setTempCoordinates({
      latitude: lat,
      longitude: lng,
      address: address
    });
    
    // Le zoom ne change PAS - c'est géré par le MapContainer
  }, []);

  const handleOpenMap = () => {
    if (disabled) return;
    setIsMapOpen(true);
    setIsEditingCoords(false);
    setGpsError(null);
    if (!tempCoordinates && !value) {
      // Définir Goma comme position par défaut
      setTempCoordinates({
        latitude: defaultCenter[0],
        longitude: defaultCenter[1],
        address: "Goma, République Démocratique du Congo"
      });
    }
  };

  const handleSaveCoordinates = async () => {
    // Arrêter le tracking GPS si actif
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsTrackingGPS(false);
    }
    
    if (tempCoordinates) {
      await onSave(tempCoordinates);
      setIsMapOpen(false);
    }
  };

  // Appliquer les coordonnées manuelles
  const handleApplyManualCoords = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) {
      alert('Veuillez entrer des coordonnées valides');
      return;
    }
    
    // Valider les plages
    if (lat < -90 || lat > 90) {
      alert('La latitude doit être entre -90 et 90');
      return;
    }
    if (lng < -180 || lng > 180) {
      alert('La longitude doit être entre -180 et 180');
      return;
    }
    
    // Mettre à jour les coordonnées
    handleMapClick(lat, lng);
    
    // Centrer la carte sur les nouvelles coordonnées SANS changer le zoom
    if (mapInstanceRef.current) {
      const currentZoom = mapInstanceRef.current.getZoom();
      mapInstanceRef.current.setView([lat, lng], currentZoom);
    }
    
    setIsEditingCoords(false);
  };

  // Obtenir la position GPS une seule fois
  const handleGetGPSOnce = () => {
    if (!navigator.geolocation) {
      setGpsError('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }

    setIsLoadingGPS(true);
    setGpsError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        
        handleMapClick(lat, lng);
        
        // Mettre à jour l'adresse avec la précision
        setTempCoordinates(prev => prev ? {
          ...prev,
          address: `Position GPS (précision: ${accuracy.toFixed(0)}m)`
        } : null);
        
        // Centrer la carte sur la position GPS avec un zoom élevé
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 17);
        }
        
        setIsLoadingGPS(false);
      },
      (error) => {
        console.error('Erreur GPS:', error);
        let errorMessage = 'Impossible d\'obtenir votre position.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permission refusée. Autorisez la géolocalisation dans votre navigateur.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Position indisponible. Vérifiez que le GPS est activé.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Délai dépassé. Réessayez.';
            break;
        }
        setGpsError(errorMessage);
        setIsLoadingGPS(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Activer/Désactiver le tracking GPS en temps réel
  const toggleGPSTracking = () => {
    if (!navigator.geolocation) {
      setGpsError('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }

    if (isTrackingGPS) {
      // Arrêter le tracking
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setIsTrackingGPS(false);
    } else {
      // Démarrer le tracking
      setIsTrackingGPS(true);
      setGpsError(null);

      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          
          setTempCoordinates({
            latitude: lat,
            longitude: lng,
            address: `Position GPS en temps réel (précision: ${accuracy.toFixed(0)}m)`
          });
          
          // Centrer la carte sur la position SANS changer le zoom
          if (mapInstanceRef.current) {
            const currentZoom = mapInstanceRef.current.getZoom();
            mapInstanceRef.current.setView([lat, lng], currentZoom);
          }
        },
        (error) => {
          console.error('Erreur tracking GPS:', error);
          setGpsError('Erreur de tracking GPS. Vérifiez les permissions.');
          setIsTrackingGPS(false);
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 1000 // Mise à jour toutes les secondes max
        }
      );
    }
  };

  const formatCoordinates = (coords: Coordinates) => {
    return `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
  };

  // Composant de carte interactive
  const InteractiveMap = () => {
    if (loadError) {
      return (
        <div className="w-full h-96 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
          <div className="text-center text-red-600">
            <Map className="w-12 h-12 mx-auto mb-2" />
            <p className="mb-2">Erreur de chargement de la carte</p>
            <p className="text-sm">{loadError}</p>
          </div>
        </div>
      );
    }

    if (!isLoaded || !MapContainer) {
      return (
        <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
          <div className="text-center">
            <Map className="w-12 h-12 text-gray-400 mx-auto mb-2 animate-pulse" />
            <p className="text-gray-500">Chargement de la carte...</p>
          </div>
        </div>
      );
    }

    const position: [number, number] = tempCoordinates 
      ? [tempCoordinates.latitude, tempCoordinates.longitude]
      : defaultCenter;

    return (
      <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-300 relative">
        <MapContainer
          center={position}
          zoom={initialZoom}
          style={{ height: '100%', width: '100%' }}
          className="rounded-lg"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEventHandler onMapClick={handleMapClick} onMapReady={handleMapReady} />
          {tempCoordinates && (
            <Marker position={[tempCoordinates.latitude, tempCoordinates.longitude]} />
          )}
        </MapContainer>
        {isTrackingGPS && (
          <div className="absolute top-2 left-2 z-[1000] bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center animate-pulse">
            <Crosshair className="w-3 h-3 mr-1" />
            Tracking GPS actif
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={handleOpenMap}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border border-gray-300 rounded-lg text-left
            focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]
            transition-colors duration-200
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:bg-gray-50 cursor-pointer'}
            ${value ? 'text-gray-900' : 'text-gray-400'}
          `}
        >
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="flex-1">
              {value ? (
                <div>
                  <div className="font-medium">{formatCoordinates(value)}</div>
                  {value.address && (
                    <div className="text-xs text-gray-500">{value.address}</div>
                  )}
                </div>
              ) : (
                placeholder
              )}
            </span>
            <Map className="w-4 h-4 text-gray-400" />
          </div>
        </button>
      </div>

      {/* Modal de carte interactive */}
      {isMapOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Sélectionner la localisation
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Cliquez sur la carte ou saisissez les coordonnées manuellement
                </p>
              </div>
              <button
                onClick={() => setIsMapOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 p-4 overflow-auto">
              {/* Boutons d'action rapide */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  type="button"
                  onClick={handleGetGPSOnce}
                  disabled={isLoadingGPS}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                >
                  {isLoadingGPS ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Navigation className="w-4 h-4 mr-1.5" />
                  )}
                  Ma position GPS
                </button>
                <button
                  type="button"
                  onClick={toggleGPSTracking}
                  className={`inline-flex items-center px-3 py-1.5 text-sm border rounded-md transition-colors ${
                    isTrackingGPS 
                      ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' 
                      : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                  }`}
                >
                  <Crosshair className={`w-4 h-4 mr-1.5 ${isTrackingGPS ? 'animate-pulse' : ''}`} />
                  {isTrackingGPS ? 'Arrêter le tracking' : 'Tracking temps réel'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingCoords(!isEditingCoords)}
                  className={`inline-flex items-center px-3 py-1.5 text-sm border rounded-md transition-colors ${
                    isEditingCoords 
                      ? 'bg-[#197ca8] text-white border-[#197ca8]' 
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Edit3 className="w-4 h-4 mr-1.5" />
                  Saisie manuelle
                </button>
              </div>

              {/* Message d'erreur GPS */}
              {gpsError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {gpsError}
                </div>
              )}

              {/* Formulaire de saisie manuelle des coordonnées */}
              {isEditingCoords && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-medium text-amber-900 mb-3">
                    Saisir les coordonnées manuellement
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Latitude (ex: -1.6792)
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={manualLat}
                        onChange={(e) => setManualLat(e.target.value)}
                        placeholder="-1.6792"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Entre -90 et 90</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Longitude (ex: 29.2228)
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={manualLng}
                        onChange={(e) => setManualLng(e.target.value)}
                        placeholder="29.2228"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Entre -180 et 180</p>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleApplyManualCoords}
                      className="px-4 py-2 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors"
                    >
                      Appliquer ces coordonnées
                    </button>
                  </div>
                </div>
              )}

              {/* Carte interactive */}
              <InteractiveMap />
              
              {/* Affichage de la position sélectionnée */}
              {tempCoordinates && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Position sélectionnée
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          <strong>Coordonnées:</strong> {formatCoordinates(tempCoordinates)}
                        </p>
                        {tempCoordinates.address && (
                          <p className="text-sm text-blue-700">
                            <strong>Adresse:</strong> {tempCoordinates.address}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingCoords(true);
                        setManualLat(tempCoordinates.latitude.toFixed(6));
                        setManualLng(tempCoordinates.longitude.toFixed(6));
                      }}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Modifier manuellement"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setIsMapOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveCoordinates}
                disabled={!tempCoordinates}
                className="px-4 py-2 bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirmer la sélection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};